import { memo, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { useLocalizedDate, useWorkspaceMemberSuggestions } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { FilterPillsList } from '@/components/ui/FilterPill';
import { resolveMemberLabels } from '@/utils';
import type { PillData } from '@/components/ui/FilterPill';

interface NoteFilterPillsProps {
  readonly showSearchPill?: boolean;
}

export const NoteFilterPills = memo(function NoteFilterPills({
  showSearchPill = false,
}: NoteFilterPillsProps) {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  const search = useGeoMarkupStore((state) => state.noteFilters.search);
  const setSearch = useGeoMarkupStore((state) => state.noteFilters.setSearch);
  const colors = useGeoMarkupStore(
    useShallow((state) => state.noteFilters.colors)
  );
  const authors = useGeoMarkupStore(
    useShallow((state) => state.noteFilters.authors)
  );
  const dateFrom = useGeoMarkupStore((state) => state.noteFilters.dateFrom);
  const dateTo = useGeoMarkupStore((state) => state.noteFilters.dateTo);
  const hasLocation = useGeoMarkupStore(
    (state) => state.noteFilters.hasLocation
  );
  const location = useGeoMarkupStore((state) => state.noteFilters.location);

  const clearColors = useGeoMarkupStore(
    (state) => state.noteFilters.clearColors
  );
  const setAuthors = useGeoMarkupStore((state) => state.noteFilters.setAuthors);
  const setDateRange = useGeoMarkupStore(
    (state) => state.noteFilters.setDateRange
  );
  const setHasLocation = useGeoMarkupStore(
    (state) => state.noteFilters.setHasLocation
  );
  const setLocation = useGeoMarkupStore(
    (state) => state.noteFilters.setLocation
  );

  // Only subscribe to the member list when an author filter is active — when
  // empty the author pill is never rendered, so the subscription/comparison is
  // pure overhead on the common (no-author-filter) path.
  const { members } = useWorkspaceMemberSuggestions({
    enabled: authors.length > 0,
  });

  // Resolve the selected author ids to member display names for the pill.
  // Fallback to the id only if the member can't be resolved, so the pill is
  // never empty.
  const authorsLabel = useMemo(
    () => resolveMemberLabels(members, authors),
    [members, authors]
  );

  const pills = useMemo(() => {
    const result: PillData[] = [];

    if (showSearchPill && search) {
      result.push({
        id: 'search',
        label: t('notes.filters.title'),
        value: search,
        onRemove: () => setSearch(''),
      });
    }

    if (colors.length > 0) {
      const colorValue =
        colors.length === 1
          ? t(`notes.filters.colorNames.${colors[0]}`, {
              defaultValue: colors[0],
            })
          : t('notes.filters.colorSelected', { count: colors.length });

      result.push({
        id: 'colors',
        label: t('notes.filters.color'),
        value: colorValue,
        onRemove: clearColors,
      });
    }

    if (authors.length > 0) {
      result.push({
        id: 'author',
        label: t('notes.filters.author'),
        value: authorsLabel ?? authors.join(', '),
        onRemove: () => setAuthors([]),
      });
    }

    if (dateFrom || dateTo) {
      const fromText = dateFrom
        ? formatDateCompact(dateFrom)
        : t('filters.none');
      const toText = dateTo ? formatDateCompact(dateTo) : t('filters.none');

      result.push({
        id: 'date-range',
        label: t('notes.filters.dateRange'),
        value: `${fromText} - ${toText}`,
        onRemove: () => setDateRange(null, null),
      });
    }

    if (hasLocation !== null) {
      result.push({
        id: 'has-location',
        label: t('notes.filters.hasLocation'),
        value: hasLocation ? t('common.yes') : t('common.no'),
        onRemove: () => setHasLocation(null),
      });
    }

    if (location.length > 0) {
      result.push({
        id: 'location',
        label: t('notes.filters.location'),
        value: location,
        onRemove: () => setLocation(''),
      });
    }

    return result;
  }, [
    showSearchPill,
    search,
    setSearch,
    colors,
    authors,
    authorsLabel,
    dateFrom,
    dateTo,
    hasLocation,
    location,
    t,
    formatDateCompact,
    clearColors,
    setAuthors,
    setDateRange,
    setHasLocation,
    setLocation,
  ]);

  return <FilterPillsList pills={pills} />;
});
